import type { StoreApiTokenSummary } from "@redotech/redo-partners-portal-rpc/schema/shared/api-token";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoStandaloneTable } from "@redotech/redo-web/arbiter-components/tables/redo-standalone-table";
import {
  type RedoTableColumn,
  RedoTableEmptyStateContent,
  RedoTableLoadingStateContent,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
  RedoTableToolbarCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Text } from "@redotech/redo-web/text";
import { type ReactNode, useState } from "react";

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "Never";
}

/**
 * Lists tokens by name and masked value, never the secret. Active tokens
 * can be revoked; revoked ones stay listed so their history is visible.
 */
export function TokenTable<T extends StoreApiTokenSummary>({
  title,
  tokens,
  extraColumns = [],
  headerButton,
  pending,
  revoke,
}: {
  title: string;
  tokens: T[];
  extraColumns?: RedoTableColumn<T>[];
  headerButton: ReactNode;
  pending: boolean;
  revoke: (tokenId: string) => Promise<void>;
}) {
  const [tokenToRevoke, setTokenToRevoke] = useState<T | undefined>();
  const [revoking, setRevoking] = useState(false);

  const confirmRevoke = async () => {
    if (!tokenToRevoke) {
      return;
    }
    setRevoking(true);
    try {
      await revoke(tokenToRevoke.id);
      toast(`Revoked "${tokenToRevoke.name}"`, { variant: "success" });
      setTokenToRevoke(undefined);
    } catch {
      toast("Couldn't revoke the token. Try again.", { variant: "error" });
    } finally {
      setRevoking(false);
    }
  };
  const columns: RedoTableColumn<T>[] = [
    {
      key: "name",
      renderHeaderCell: () => <RedoTableTextCell text="Name" />,
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell
          leading
          subtext={`${row.tokenPrefix}••••${row.tokenSuffix}`}
          text={row.name}
        />
      ),
    },
    ...extraColumns,
    {
      key: "created",
      renderHeaderCell: () => <RedoTableTextCell text="Created" />,
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={formatDate(row.createdAt)} />
      ),
    },
    {
      key: "last-used",
      renderHeaderCell: () => <RedoTableTextCell text="Last used" />,
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={formatDate(row.lastUsedAt)} />
      ),
    },
    {
      key: "actions",
      widthMode: { type: "hug" },
      renderHeaderCell: () => null,
      renderNormalCell: ({ row }) =>
        row.revokedAt ? (
          <RedoTablePaddedCell>
            <RedoBadge color="gray" size="sm" text="Revoked" />
          </RedoTablePaddedCell>
        ) : (
          <RedoTableToolbarCell
            buttons={[
              {
                hierarchy: "secondary",
                text: "Revoke",
                onClick: () => setTokenToRevoke(row),
              },
            ]}
          />
        ),
    },
  ];

  return (
    <>
      <RedoStandaloneTable
        emptyStateContent={
          pending ? (
            <RedoTableLoadingStateContent />
          ) : (
            <RedoTableEmptyStateContent text="No tokens yet" />
          )
        }
        header={{ title, button: headerButton }}
        layout={{ columns }}
        tableData={tokens}
      />
      {tokenToRevoke && (
        <RedoModal
          onModalCloseRequested={() => setTokenToRevoke(undefined)}
          primaryButton={{
            text: "Revoke",
            onClick: confirmRevoke,
            pending: revoking,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setTokenToRevoke(undefined),
          }}
          theme="warn"
          title={`Revoke "${tokenToRevoke.name}"?`}
        >
          <Text fontSize="sm" m="none" textColor="secondary">
            Anything using this token stops working at once. This can't be
            undone.
          </Text>
        </RedoModal>
      )}
    </>
  );
}
